import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import ProductList from '../shared/ProductList';
import MoloRingloader from '../layout/MoloRingloader';
import Style, { createStyle } from 'molo-shared/lib/models/Style';
import { wishlistItemKey } from '../../store/wishlist/wishlistKey';

import { RootState } from '../../store';
import { IUserSate } from '../../store/user/reducers';
import { IFilterState } from '../../store/session/reducers';
import { IWishlistState } from '../../store/wishlist/reducers';
import { DEFAULT_WISHLIST_NAME } from '../../constants/wishlistConfig';
import WishlistApi from '../../store/wishlist/WishlistApi';
import { WishListProductDto } from '../../store/wishlist/wishlistProductTypes';
import { setDeliveryWindows } from '../../store/collections/actions';
import { applyFilter } from '../../store/session/actions';
import { clearWishlist, fetchWishlistItems } from '../../store/wishlist/actions';
import { Filter } from '../../models/Filter';
import { buildWishlistGridFilter } from '../../utils/wishlistViewFilter';
import { resolveWishlistShopBackTarget } from '../../utils/lastShopReturnContext';
import { isB2bProductOverlayOpen } from '../../utils/productOverlayHistory';
import { FilterHelper } from '../../utils/FilterHelper';
import { filterPurchasableStyles } from '../../utils/styleAvailability';
import Modal from '../layout/Modal/Modal';
import OrderApi from '../../store/order/OrderApi';
import { IOrderState } from '../../store/order/orderStore';
import {
    buildOtherOpenOrdersByKey,
    masterColorItemsFromStyles,
    OpenOrderBasketHintDto,
    OtherOpenOrderRef,
} from '../../utils/openOrderBasketHints';

interface OwnProps extends RouteComponentProps<{ listname: string }> {}

interface StateProps {
    userState: IUserSate;
    filterState: IFilterState;
    wishlist: IWishlistState;
    orderState: IOrderState;
}

interface DispatchProps {
    setDeliveryWindows: (styles: Style[]) => void;
    applyFilter: (filter: Filter) => Promise<void>;
    clearWishlist: (listname: string) => Promise<void>;
    fetchWishlistItems: () => Promise<void>;
}

interface State {
    products: Style[];
    loading: boolean;
    error: string | null;
    showClearWishlistModal: boolean;
    otherOpenOrdersByKey: Record<string, OtherOpenOrderRef[]>;
}

type Props = OwnProps & StateProps & DispatchProps;

export class WishlistProductListContainer extends Component<Props, State> {
    isRefreshing = false;
    private openOrderHintsRequestId = 0;

    state: State = {
        products: [],
        loading: true,
        error: null,
        showClearWishlistModal: false,
        otherOpenOrdersByKey: {},
    };

    getActiveAppOrderId = (): number | null => {
        const order = this.props.orderState.order;
        if (!order || !order.orderheader) {
            return null;
        }
        const id = order.orderheader.appOrderId;
        return id != null ? id : null;
    };

    loadOpenOrderBasketHints = async (products: Style[]) => {
        const items = masterColorItemsFromStyles(products);
        if (!items.length) {
            this.setState({ otherOpenOrdersByKey: {} });
            return;
        }

        const requestId = ++this.openOrderHintsRequestId;
        try {
            const response = await OrderApi.getOpenOrderBasketHints(items);
            if (requestId !== this.openOrderHintsRequestId) {
                return;
            }
            const hints = (response.data || []) as OpenOrderBasketHintDto[];
            const otherOpenOrdersByKey = buildOtherOpenOrdersByKey(
                hints,
                this.getActiveAppOrderId()
            );
            this.setState({ otherOpenOrdersByKey: otherOpenOrdersByKey });
        } catch (err) {
            if (requestId !== this.openOrderHintsRequestId) {
                return;
            }
            console.warn('[b2b] GetOpenOrderBasketHints failed', err);
            this.setState({ otherOpenOrdersByKey: {} });
        }
    };

    componentDidMount() {
        this.loadWishlistProducts();
        document.addEventListener('keydown', this.handleWishlistKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleWishlistKeyDown);
    }

    handleWishlistKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') {
            return;
        }

        if (this.state.showClearWishlistModal) {
            return;
        }

        if (isB2bProductOverlayOpen()) {
            return;
        }

        event.preventDefault();
        const { pathname } = resolveWishlistShopBackTarget();
        this.props.history.push(pathname);
    };

    componentDidUpdate(prevProps: Props) {
        const prevName = prevProps.match && prevProps.match.params ? prevProps.match.params.listname : '';
        const name = this.props.match && this.props.match.params ? this.props.match.params.listname : '';
        if (name && name !== prevName) {
            this.setState({ loading: true, products: [], error: null, otherOpenOrdersByKey: {} }, () =>
                this.loadWishlistProducts()
            );
            return;
        }

        const prevOrder = prevProps.orderState.order;
        const nextOrder = this.props.orderState.order;
        const prevAppId =
            prevOrder && prevOrder.orderheader ? prevOrder.orderheader.appOrderId : null;
        const nextAppId =
            nextOrder && nextOrder.orderheader ? nextOrder.orderheader.appOrderId : null;
        const syncTokenChanged =
            prevProps.orderState.remoteOrderSyncToken !==
            this.props.orderState.remoteOrderSyncToken;
        if (
            this.state.products.length > 0 &&
            (syncTokenChanged || prevAppId !== nextAppId || prevOrder !== nextOrder)
        ) {
            void this.loadOpenOrderBasketHints(this.state.products);
        }

        const listname = name ? decodeURIComponent(name) : '';
        if (listname !== DEFAULT_WISHLIST_NAME) {
            return;
        }

        const prevKeys = prevProps.wishlist.itemKeys;
        const keys = this.props.wishlist.itemKeys;
        if (prevKeys === keys) {
            return;
        }

        const addedKeys = Object.keys(keys).filter((key) => !prevKeys[key]);
        if (addedKeys.length > 0) {
            this.setState({ loading: true, error: null }, () => this.loadWishlistProducts());
            return;
        }

        const prevKeyCount = Object.keys(prevKeys).length;
        const nextKeyCount = Object.keys(keys).length;
        if (prevKeyCount === 0 && nextKeyCount === 0) {
            return;
        }

        const nextProducts = this.state.products.filter((p) =>
            !!keys[wishlistItemKey(p.masterId, p.colorCode)]
        );
        if (
            nextProducts.length === this.state.products.length &&
            nextProducts.every((p, i) => p === this.state.products[i])
        ) {
            return;
        }

        this.setState({ products: nextProducts }, () => {
            void this.pushWishlistFilterForStyles(nextProducts);
        });
    }

    pushWishlistFilterForStyles = async (styles: Style[]) => {
        const current = this.props.filterState.filter;
        const next = buildWishlistGridFilter(styles, current || undefined);
        await this.props.applyFilter(next);
    };

    /** ProductList `simple` mode uses props.products only — apply Redux filter here. */
    getDisplayProducts = (): Style[] => {
        const purchasable = filterPurchasableStyles(this.state.products);
        const f = this.props.filterState.filter;
        if (!f) {
            return purchasable;
        }
        return FilterHelper.resultByFilter(f, purchasable);
    };

    handleOpenClearWishlistModal = () => {
        const raw = this.props.match && this.props.match.params && this.props.match.params.listname;
        const listname = raw ? decodeURIComponent(raw) : '';
        if (!listname || !this.state.products.length || this.props.wishlist.loading) {
            return;
        }
        this.setState({ showClearWishlistModal: true });
    };

    handleCloseClearWishlistModal = () => {
        this.setState({ showClearWishlistModal: false });
    };

    handleRefreshWishlist = async () => {
        if (this.isRefreshing || this.props.wishlist.loading) {
            return;
        }

        this.isRefreshing = true;
        this.setState({ loading: true, error: null });

        try {
            await this.props.fetchWishlistItems();
            await this.loadWishlistProducts();
        } finally {
            this.isRefreshing = false;
        }
    };

    handleConfirmClearWishlist = () => {
        const raw = this.props.match && this.props.match.params && this.props.match.params.listname;
        const listname = raw ? decodeURIComponent(raw) : '';
        if (!listname || !this.state.products.length) {
            this.setState({ showClearWishlistModal: false });
            return;
        }
        this.setState({ showClearWishlistModal: false });
        void this.props
            .clearWishlist(listname)
            .then(() => {
                void this.loadWishlistProducts();
            })
            .catch(() => {
                /* error surfaced via setAlert in thunk */
            });
    };

    loadWishlistProducts = async () => {
        const raw = this.props.match && this.props.match.params && this.props.match.params.listname;
        const listname = raw ? decodeURIComponent(raw) : '';

        if (!listname) {
            this.setState({ loading: false, error: 'Missing list name', products: [] });
            return;
        }

        const user = this.props.userState.user;
        if (!user) {
            this.setState({ loading: false, error: null, products: [] });
            return;
        }

        try {
            const res = await WishlistApi.getWishList({ listname });
            const rows: WishListProductDto[] = Array.isArray(res.data)
                ? (res.data as WishListProductDto[])
                : [];
            const styles: Style[] = [];

            for (const row of rows) {
                if (!row || !row.product) {
                    continue;
                }
                styles.push(createStyle(row.product));
            }

            const purchasableStyles = filterPurchasableStyles(styles);

            if (purchasableStyles.length) {
                this.props.setDeliveryWindows(purchasableStyles);
            }

            await this.pushWishlistFilterForStyles(purchasableStyles);

            this.setState(
                {
                    products: purchasableStyles,
                    loading: false,
                    error: null,
                },
                () => {
                    void this.loadOpenOrderBasketHints(purchasableStyles);
                }
            );
        } catch {
            this.setState({
                loading: false,
                error: 'Could not load wishlist',
                products: [],
                otherOpenOrdersByKey: {},
            });
        }
    };

    render() {
        const { loading, error } = this.state;
        const user = this.props.userState.user;

        if (!user) {
            return null;
        }

        const clearCount = this.state.products.length;

        return (
            <div className="wishlist-page">
                <Modal
                    isShowing={this.state.showClearWishlistModal}
                    hide={this.handleCloseClearWishlistModal}
                    size="sm"
                    bodyClasses="confirm-modal wishlist-clear-modal"
                    hideFooter={true}
                    header={
                        <span className="wishlist-clear-modal__title">Clear wishlist?</span>
                    }
                >
                    <div className="wishlist-clear-modal__body">
                        <p className="wishlist-clear-modal__lead">
                            All products will be removed from this list. You can add them again anytime.
                        </p>
                        <p className="wishlist-clear-modal__count">
                            {clearCount} {clearCount === 1 ? 'item' : 'items'} will be removed.
                        </p>
                        <div className="wishlist-clear-modal__footer">
                            <button
                                type="button"
                                className="btn wishlist-clear-modal__btn wishlist-clear-modal__btn--secondary"
                                onClick={this.handleCloseClearWishlistModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn wishlist-clear-modal__btn wishlist-clear-modal__btn--danger"
                                onClick={this.handleConfirmClearWishlist}
                                disabled={this.props.wishlist.loading}
                            >
                                Clear wishlist
                            </button>
                        </div>
                    </div>
                </Modal>
                {loading && (
                    <div style={{ height: '100vh', width: '100vw', position: 'fixed' }}>
                        <MoloRingloader />
                    </div>
                )}
                {!loading && error && (
                    <div className="loading-spinner-1" style={{ padding: '2rem' }}>
                        <h2>{error}</h2>
                    </div>
                )}
                {!loading && !error && (
                    <ProductList
                        products={this.getDisplayProducts()}
                        lineDiscount={user.customerData.linediscount}
                        useInfiniteScroll={true}
                        simple={true}
                        simpleFloatingCategoryTitle="Favorites"
                        groupSimpleListBySeason={true}
                        highlightIncompatibleSeasons={true}
                        otherOpenOrdersByKey={this.state.otherOpenOrdersByKey}
                        useProductOrderType={false}
                        lazyLoadMoloMaster={false}
                        showGoBack={true}
                        ignoreFilter={true}
                        checkOrderType={true}
                        useAllOrderTypesForMasterColor={true}
                        renderExtraToolbarRow={
                            <div className="filter-dropdown__toggler__wrapper wishlist-toolbar-actions">
                                <div
                                    className={
                                        'filter-dropdown__toggler' +
                                        (this.props.wishlist.loading || this.isRefreshing
                                            ? ' filter-dropdown__toggler--disabled'
                                            : '')
                                    }
                                    onClick={() => {
                                        if (this.props.wishlist.loading || this.isRefreshing) {
                                            return;
                                        }
                                        void this.handleRefreshWishlist();
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') {
                                            return;
                                        }
                                        e.preventDefault();
                                        if (this.props.wishlist.loading || this.isRefreshing) {
                                            return;
                                        }
                                        void this.handleRefreshWishlist();
                                    }}
                                >
                                    <span className="filter-dropdown__toggler__title">Update</span>
                                </div>
                                <div
                                    className={
                                        'filter-dropdown__toggler' +
                                        (!this.state.products.length || this.props.wishlist.loading
                                            ? ' filter-dropdown__toggler--disabled'
                                            : '')
                                    }
                                    onClick={() => {
                                        if (
                                            !this.state.products.length ||
                                            this.props.wishlist.loading
                                        ) {
                                            return;
                                        }
                                        this.handleOpenClearWishlistModal();
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') {
                                            return;
                                        }
                                        e.preventDefault();
                                        if (
                                            !this.state.products.length ||
                                            this.props.wishlist.loading
                                        ) {
                                            return;
                                        }
                                        this.handleOpenClearWishlistModal();
                                    }}
                                >
                                    <span className="filter-dropdown__toggler__title">Clear all</span>
                                </div>
                            </div>
                        }
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = (states: RootState): StateProps => ({
    userState: states.user.user,
    filterState: states.session.filter,
    wishlist: states.wishlist,
    orderState: states.order.order,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>): DispatchProps => ({
    setDeliveryWindows: async (styles: Style[]) => {
        await dispatch(setDeliveryWindows(styles));
    },
    applyFilter: async (f: Filter) => {
        await dispatch(applyFilter(f));
    },
    clearWishlist: async (listname: string) => {
        await dispatch(clearWishlist(listname));
    },
    fetchWishlistItems: async () => {
        await dispatch(fetchWishlistItems());
    },
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(WishlistProductListContainer)
);
